import { TMasterOccupation } from "@/api/entities/master-occupation.entity";
import { useCreateMasterOccupation, useGetMasterOccupationTree, useUpdateMasterOccupation } from "@/api/services/global/master";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EMPTY_STR } from "@/constants/string";
import { ShokushuFormTable } from "@/features/master-data/components/Occupation/ShokushuFormTable";
import { OCCUPATION_TYPE } from "@/features/master-data/constants/occupationType";
import { useConfirmCloseModal } from "@/features/master-data/hooks/useConfirmCloseModal";
import { masterDataOccupationNameSchema } from "@/features/master-data/schema/masterDataOccupationSchema";
import { TMasterDataOccupationChildFormValues } from "@/features/master-data/types/masterDataOccupationForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TCategoryFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** 編集対象のカテゴリ。未指定なら新規追加 */
  selectedCategory?: TMasterOccupation;
};

export const CategoryFormModal: FC<TCategoryFormModalProps> = ({ isOpen, onClose, selectedCategory }) => {
  const createMutation = useCreateMasterOccupation();
  const updateMutation = useUpdateMasterOccupation();

  const { data: categories } = useGetMasterOccupationTree();
  // 保存後は再取得したツリーから引き直す（作成・更新 API は id しか返さないため）
  const shokushuRows = useMemo(
    () => categories.find((item) => toNumber(item.id) === toNumber(selectedCategory?.id))?.children ?? [],
    [categories, selectedCategory],
  );

  const methods = useForm<TMasterDataOccupationChildFormValues>({
    resolver: yupResolver(masterDataOccupationNameSchema),
    mode: "onBlur",
    defaultValues: { name: selectedCategory?.name ?? undefined },
  });

  const {
    control,
    formState: { isDirty },
  } = methods;
  const { confirmClose, handleRequestClose } = useConfirmCloseModal(isDirty, onClose);

  const onSubmit = async (data: TMasterDataOccupationChildFormValues) => {
    try {
      const rs = selectedCategory
        ? await updateMutation.mutateAsync({ id: toNumber(selectedCategory.id), payload: { name: data.name } })
        : await createMutation.mutateAsync({ name: data.name, type: OCCUPATION_TYPE.CATEGORY });

      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={1200}
      title={`カテゴリの${selectedCategory ? "編集" : "追加"}`}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataOccupationNameSchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField control={control} name="name" label="カテゴリ">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={12}>
            <Stack gap={2}>
              <Typography variant="body14Bold">職種</Typography>
              <ShokushuFormTable
                shokushuRows={shokushuRows}
                parentCategoryId={selectedCategory ? toNumber(selectedCategory.id) : undefined}
                parentCategoryName={selectedCategory?.name ?? EMPTY_STR.TEXT}
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={handleRequestClose}>
            キャンセル
          </Button>
          <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
            保存
          </Button>
        </Stack>
      </Form>
      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
    </Modal>
  );
};
